/**
 * ViewAsOrgButton Component
 * 
 * Action button that allows super admins to view the system as a specific organization.
 * Includes a modal for providing a reason for the impersonation (for audit logging).
 */

import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { Modal, Button } from '@/components/ui';
import { Eye } from 'lucide-react';

interface ViewAsOrgButtonProps {
  organizationId: string;
  organizationName: string;
  disabled?: boolean;
  onSuccess?: () => void;
  compact?: boolean;
}

export const ViewAsOrgButton: React.FC<ViewAsOrgButtonProps> = ({
  organizationId,
  organizationName,
  disabled = false,
  onSuccess,
  compact = false,
}) => {
  const { isSuperAdmin, viewAsOrganization } = useAuth();
  const { success, error: showError } = useToast();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Only super admins can view as org
  if (!isSuperAdmin) {
    return null;
  }

  const handleViewAsOrg = async () => {
    if (!reason.trim()) {
      showError('Please provide a reason for viewing as organization');
      return;
    }

    setIsLoading(true);
    try {
      await viewAsOrganization(organizationId, reason);
      success(`Now viewing as: ${organizationName}`);
      setIsModalOpen(false);
      setReason('');
      onSuccess?.();
    } catch (error: any) {
      console.error('Failed to view as org:', error);
      showError(error.message || 'Failed to view as organization');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setIsModalOpen(true)}
        disabled={disabled}
        className={compact
          ? "flex items-center gap-1 px-2 py-1 text-xs text-accent-blue hover:bg-accent-blue/10 rounded transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
          : "p-2 text-accent-blue hover:bg-accent-blue/10 rounded transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
        }
        title="View as Organization"
      >
        <Eye className="h-3.5 w-3.5" />
        {compact && <span>View</span>}
      </button>

      {/* Confirmation Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setReason('');
        }}
        title="View as Organization"
        size="md"
      >
        <div className="space-y-4">
          <div className="p-4 bg-status-warning-bg border border-status-warning rounded-lg">
            <p className="text-sm text-status-warning">
              ⚠️ <strong>Warning:</strong> You are about to view the system as{' '}
              <strong>{organizationName}</strong>. This action will be logged for security and compliance.
            </p>
          </div>

          <div>
            <p className="text-sm font-medium text-foreground mb-2">
              Organization:
            </p>
            <p className="text-base font-semibold text-foreground">
              {organizationName}
            </p>
          </div>

          <div>
            <label
              htmlFor="reason"
              className="block text-sm font-medium text-foreground mb-2"
            >
              Reason (required) *
            </label>
            <textarea
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g., Investigating user-reported issue, providing customer support..."
              rows={3}
              className="w-full px-3 py-2 border border-border rounded-lg
                       bg-background
                       text-foreground
                       placeholder:text-muted-foreground
                       focus:ring-2 focus:ring-primary-500 focus:border-transparent
                       transition-colors duration-150"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              This reason will be recorded in the audit logs for compliance and security purposes.
            </p>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button
              variant="secondary"
              onClick={() => {
                setIsModalOpen(false);
                setReason('');
              }}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={handleViewAsOrg}
              disabled={isLoading || !reason.trim()}
            >
              {isLoading ? 'Starting...' : 'View as Organization'}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

